import { ElLoading } from 'element-plus';
import { defineStore } from 'pinia';
import { LocationQueryValue } from 'vue-router';
import * as Sentry from '@sentry/vue';

import { LOCAL_STORAGE_AUTH_ID } from '@/constants';
import { CurrentUser } from '@/types/user';
import { login, logout } from '@/utils/api/auth';
import { gtmUpdateUser } from '@/utils/helper/gtm';

export interface StoreUser {
	currentUser: CurrentUser;
	isLoggedIn: boolean;
	authToken: string;
	isVerified2FA: boolean;
	twoFaToken: string;
	countdownResend: number;
	countdownLockVerify: number;
	countdownLockResend: number;
	remainingResends: number;
}

const defaultStoreUser: StoreUser = {
	currentUser: {
		email: '',
		userId: '',
		country: '',
		avatar: '',
	},
	isLoggedIn: false,
	authToken: '',
	isVerified2FA: false,
	twoFaToken: '',
	countdownResend: 0,
	countdownLockVerify: 0,
	countdownLockResend: 0,
	remainingResends: 0,
};

export const useUserStore = defineStore({
	id: 'userStore',
	state: (): StoreUser => {
		try {
			const ls = localStorage.getItem(LOCAL_STORAGE_AUTH_ID) || '{}';
			const storageUser = JSON.parse(ls);
			const { authToken, currentUser } = storageUser;
			if (!authToken) {
				gtmUpdateUser();
				return defaultStoreUser;
			}

			gtmUpdateUser(undefined, currentUser.userId, currentUser.country);
			const _state = useMergeWith(useCloneDeep(defaultStoreUser), storageUser, (objValue, srcValue) => {
				if (!srcValue) return objValue;
				return srcValue;
			});
			return _state;
		} catch (_) {
			gtmUpdateUser();
			return defaultStoreUser;
		}
	},
	actions: {
		async performLogin(
			code: LocationQueryValue | LocationQueryValue[],
			state: LocationQueryValue | LocationQueryValue[],
		) {
			try {
				const resp = await login(code, state);
				const { token, country, email, userId, avatar } = resp;
				const currentUser = {
					email,
					userId,
					country,
					avatar,
				};
				if (token && userId) {
					this.updateLoggedIn(true);
					this.updateCurrentUser(currentUser);
					this.updateAuthToken(token);
					gtmUpdateUser('login', userId, country);
				} else {
					this.clearStore();
				}
			} catch (error) {
				this.clearStore();
				console.log({ error });
				Sentry.captureException(error);
			}
		},
		updateLoggedIn(value: boolean) {
			this.isLoggedIn = value;
			this.persistenceState();
		},
		updateIsVerified2FA(value: boolean) {
			this.isVerified2FA = value;
			this.persistenceState();
		},
		updateTwoFaToken(value: string) {
			this.twoFaToken = value;
			this.persistenceState();
		},
		updateCurrentUser(value: CurrentUser) {
			this.currentUser = value;
			this.persistenceState();
		},
		updateAuthToken(value: string) {
			this.authToken = value;
			this.persistenceState();
		},
		updateCountdownResend(value: number) {
			this.countdownResend = value;
		},
		updateCountdownLockVerify(value: number) {
			this.countdownLockVerify = value;
		},
		updateCountdownLockResend(value: number) {
			this.countdownLockResend = value;
		},
		updateRemainingResends(value: number) {
			this.remainingResends = value;
		},
		persistenceState() {
			localStorage.setItem(LOCAL_STORAGE_AUTH_ID, JSON.stringify(this.$state));
		},
		async logout() {
			const loadingInstance = ElLoading.service({ fullscreen: true });
			try {
				await logout();
				gtmUpdateUser('logout', this.currentUser.userId, this.currentUser.country);
			} catch (error) {
				console.log({ error });
				Sentry.captureException(error);
			} finally {
				this.clearStore();
				loadingInstance.close();
			}
		},
		clearStore() {
			const { isLoggedIn, currentUser, authToken } = useCloneDeep(defaultStoreUser);
			this.updateLoggedIn(isLoggedIn);
			this.updateCurrentUser(currentUser);
			this.updateAuthToken(authToken);
			gtmUpdateUser();
		},
	},
	getters: {
		getLoggedIn: (state) => state.isLoggedIn,
		getIsVerified2FA: (state) => state.isVerified2FA,
		getAuthToken: (state) => state.authToken,
		getTwoFaToken: (state) => state.twoFaToken,
		getUserInfo: (state) => state.currentUser,
		getCountdownResend: (state) => state.countdownResend,
		getCountdownLockVerify: (state) => state.countdownLockVerify,
		getCountdownLockResend: (state) => state.countdownLockResend,
		getRemainingResends: (state) => state.remainingResends,
		getUserAvatar: (state) => state.currentUser.avatar,
		getUserEmail: (state) => state.currentUser.email,
	},
});
